import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Download, Table2, TrendingDown, TrendingUp } from 'lucide-react'
import {
  formatMoney, formatNumber, GRANULARITIES, REPORT_PERIODS,
  type Granularity, type ReportPeriod,
} from '@najah/core'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Input, Select, Spinner,
  Table, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import { downloadReport, reportsApi } from '../lib/reportsApi'
import { LineChart } from '../components/charts/LineChart'
import { BarChart } from '../components/charts/BarChart'

type Tab = 'sales' | 'products' | 'profit' | 'inventory' | 'tax'

const TABS: { key: Tab; label: string }[] = [
  { key: 'sales', label: 'المبيعات' },
  { key: 'products', label: 'المنتجات' },
  { key: 'profit', label: 'الأرباح والخسائر' },
  { key: 'inventory', label: 'المخزون' },
  { key: 'tax', label: 'الضريبة' },
]

function StatTile({
  label, value, hint, delta,
}: { label: string; value: string; hint?: string; delta?: { value: number; goodWhenUp?: boolean } }) {
  const up = (delta?.value ?? 0) >= 0
  const good = delta ? (delta.goodWhenUp === false ? !up : up) : true

  return (
    <Card>
      <CardBody>
        <p className="text-sm text-fg-muted">{label}</p>
        {/* أرقام العرض بعرض حرف طبيعي لا tabular — tabular للأعمدة فقط */}
        <p className="mt-1 text-2xl font-semibold tracking-tight text-fg" dir="ltr">{value}</p>
        {delta && (
          <p className={cn('mt-1 flex items-center gap-1 text-xs', good ? 'text-success' : 'text-danger')}>
            {up ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />}
            <span dir="ltr">{Math.abs(delta.value).toFixed(1)}%</span>
          </p>
        )}
        {hint && !delta && <p className="mt-1 text-xs text-fg-subtle">{hint}</p>}
      </CardBody>
    </Card>
  )
}

export function ReportsPage() {
  const toast = useToast()
  const [tab, setTab] = useState<Tab>('sales')
  const [period, setPeriod] = useState<ReportPeriod>('month')
  const [granularity, setGranularity] = useState<Granularity>('day')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [stagnantDays, setStagnantDays] = useState(90)
  const [showTable, setShowTable] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const range = { period, granularity, ...(period === 'custom' ? { from, to } : {}) }
  const rangeKey = JSON.stringify(range)

  const { data: summary } = useQuery({
    queryKey: ['reports', 'summary', rangeKey],
    queryFn: () => reportsApi.summary(range),
    placeholderData: keepPreviousData,
  })
  const { data: series, isPending: seriesPending } = useQuery({
    queryKey: ['reports', 'series', rangeKey],
    queryFn: () => reportsApi.series(range),
    placeholderData: keepPreviousData,
  })
  const { data: products } = useQuery({
    queryKey: ['reports', 'products', rangeKey],
    queryFn: () => reportsApi.products(range),
    enabled: tab === 'products',
    placeholderData: keepPreviousData,
  })
  const { data: cashiers } = useQuery({
    queryKey: ['reports', 'cashiers', rangeKey],
    queryFn: () => reportsApi.cashiers(range),
    enabled: tab === 'products',
    placeholderData: keepPreviousData,
  })
  const { data: pl } = useQuery({
    queryKey: ['reports', 'pl', rangeKey],
    queryFn: () => reportsApi.profitLoss(range),
    enabled: tab === 'profit',
    placeholderData: keepPreviousData,
  })
  const { data: purchases } = useQuery({
    queryKey: ['reports', 'purchases', rangeKey],
    queryFn: () => reportsApi.purchases(range),
    enabled: tab === 'profit',
  })
  const { data: valuation } = useQuery({
    queryKey: ['reports', 'valuation'],
    queryFn: reportsApi.valuation,
    enabled: tab === 'inventory',
  })
  const { data: stagnant } = useQuery({
    queryKey: ['reports', 'stagnant', stagnantDays],
    queryFn: () => reportsApi.stagnant(stagnantDays),
    enabled: tab === 'inventory',
    placeholderData: keepPreviousData,
  })
  const { data: tax } = useQuery({
    queryKey: ['reports', 'tax', rangeKey],
    queryFn: () => reportsApi.tax(range),
    enabled: tab === 'tax',
  })

  async function handleExport(report: 'products' | 'cashiers' | 'stagnant' | 'series' | 'tax') {
    setDownloading(true)
    try {
      await downloadReport(report, report === 'stagnant' ? { days: stagnantDays } : range)
      toast.success('نُزّل التقرير', 'ملف CSV يفتح في Excel')
    } catch {
      toast.error('تعذّر التصدير')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold text-fg">التقارير</h1>
        <p className="mt-0.5 text-sm text-fg-muted">أرقام المكتبة خلال {REPORT_PERIODS[period]}</p>
      </div>

      {/* المرشّحات في صفّ واحد فوق التقارير */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-4">
        <div className="w-44">
          <Select
            label="الفترة"
            value={period} onChange={(e) => setPeriod(e.target.value as ReportPeriod)}
            options={Object.entries(REPORT_PERIODS).map(([value, label]) => ({ value, label }))}
          />
        </div>
        {period === 'custom' && (
          <>
            <div className="w-40">
              <Input label="من" type="date" ltr value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="w-40">
              <Input label="إلى" type="date" ltr value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </>
        )}
        <div className="w-36">
          <Select
            label="التجميع"
            value={granularity} onChange={(e) => setGranularity(e.target.value as Granularity)}
            options={Object.entries(GRANULARITIES).map(([value, label]) => ({ value, label }))}
          />
        </div>
      </div>

      {/* التبويبات */}
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 scrollbar-none">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              'shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors',
              tab === t.key
                ? 'border-primary bg-primary text-fg-on-primary'
                : 'border-border bg-surface text-fg-muted hover:bg-surface-hover',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ المبيعات ══ */}
      {tab === 'sales' && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile label="الإيراد" value={formatMoney(summary?.revenue ?? 0)} hint="بعد خصم المرتجعات" />
            <StatTile label="الربح الإجمالي" value={formatMoney(summary?.grossProfit ?? 0)}
              hint={summary?.marginPercent != null ? `هامش ${summary.marginPercent.toFixed(1)}%` : undefined} />
            <StatTile label="عدد الفواتير" value={formatNumber(summary?.invoiceCount ?? 0)}
              hint={`متوسّط ${formatMoney(summary?.averageTicket ?? 0)}`} />
            <StatTile label="القطع المباعة" value={formatNumber(summary?.itemsSold ?? 0)}
              hint={summary?.refundTotal ? `مرتجعات ${formatMoney(summary.refundTotal)}` : undefined} />
          </div>

          <Card>
            <CardHeader
              title="الإيراد والربح"
              description="الأقدم يميناً والأحدث يساراً — مع اتجاه القراءة"
              action={
                <div className="flex gap-1">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setShowTable((v) => !v)}
                    startIcon={<Table2 className="size-4" />}
                  >
                    {showTable ? 'الرسم' : 'جدول'}
                  </Button>
                  <Button
                    variant="secondary" size="sm"
                    onClick={() => void handleExport('series')}
                    loading={downloading}
                    startIcon={<Download className="size-4" />}
                  >
                    CSV
                  </Button>
                </div>
              }
            />
            <CardBody>
              {seriesPending ? (
                <div className="grid h-64 place-items-center"><Spinner className="size-6 text-primary" /></div>
              ) : showTable ? (
                /* عرض جدولي بديل — القيم كلها متاحة نصّياً لا بالرسم وحده */
                <TableWrapper>
                  <Table>
                    <Thead>
                      <tr>
                        <Th>الفترة</Th>
                        <Th align="center">الفواتير</Th>
                        <Th align="end">الإيراد</Th>
                        <Th align="end">التكلفة</Th>
                        <Th align="end">الربح</Th>
                      </tr>
                    </Thead>
                    <Tbody>
                      {series?.map((p) => (
                        <Tr key={p.bucket}>
                          <Td>{p.label}</Td>
                          <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(p.invoiceCount)}</Td>
                          <Td align="end" className="tabular-nums" dir="ltr">{formatMoney(p.revenue)}</Td>
                          <Td align="end" className="tabular-nums text-fg-muted" dir="ltr">{formatMoney(p.cogs)}</Td>
                          <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(p.profit)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableWrapper>
              ) : (
                <LineChart
                  labels={(series ?? []).map((p) => p.label)}
                  series={[
                    {
                      key: 'revenue', label: 'الإيراد', color: 'var(--chart-1)',
                      values: (series ?? []).map((p) => p.revenue),
                    },
                    {
                      key: 'profit', label: 'الربح', color: 'var(--chart-2)',
                      values: (series ?? []).map((p) => p.profit),
                    },
                  ]}
                  emptyText="لا توجد مبيعات في هذه الفترة"
                />
              )}
            </CardBody>
          </Card>
        </>
      )}

      {/* ══ المنتجات والموظّفون ══ */}
      {tab === 'products' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader
              title="الأكثر إيراداً"
              description="أعلى ١٠ منتجات في الفترة"
              action={
                <Button variant="secondary" size="sm" onClick={() => void handleExport('products')}
                  loading={downloading} startIcon={<Download className="size-4" />}>
                  CSV
                </Button>
              }
            />
            <CardBody>
              <BarChart
                data={(products ?? []).map((p) => ({
                  key: p.productId,
                  label: p.nameAr,
                  value: p.revenue,
                  sublabel: `${formatNumber(p.quantitySold)} قطعة`,
                }))}
                emptyText="لا توجد مبيعات في هذه الفترة"
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="الأكثر ربحاً" description="الربح لا الإيراد — قد يختلف الترتيب" />
            <CardBody>
              <BarChart
                color="var(--chart-2)"
                data={[...(products ?? [])]
                  .sort((a, b) => b.profit - a.profit)
                  .map((p) => ({
                    key: p.productId,
                    label: p.nameAr,
                    value: p.profit,
                    sublabel: p.marginPercent != null ? `${p.marginPercent.toFixed(0)}%` : undefined,
                  }))}
                emptyText="لا توجد مبيعات في هذه الفترة"
              />
            </CardBody>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader
              title="أداء الموظّفين"
              action={
                <Button variant="secondary" size="sm" onClick={() => void handleExport('cashiers')}
                  loading={downloading} startIcon={<Download className="size-4" />}>
                  CSV
                </Button>
              }
            />
            <TableWrapper className="rounded-none border-0 border-t">
              <Table>
                <Thead>
                  <tr>
                    <Th>الموظّف</Th>
                    <Th align="center">الفواتير</Th>
                    <Th align="end">الإيراد</Th>
                    <Th align="end">متوسّط الفاتورة</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {cashiers?.map((c) => (
                    <Tr key={c.userId}>
                      <Td className="font-medium text-fg">{c.fullName}</Td>
                      <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(c.invoiceCount)}</Td>
                      <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(c.revenue)}</Td>
                      <Td align="end" className="tabular-nums text-fg-muted" dir="ltr">{formatMoney(c.averageTicket)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableWrapper>
          </Card>
        </div>
      )}

      {/* ══ الأرباح والخسائر ══ */}
      {tab === 'profit' && pl && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile label="الإيراد" value={formatMoney(pl.revenue)} />
            <StatTile label="تكلفة البضاعة المباعة" value={formatMoney(pl.cogs)} />
            <StatTile label="الربح الإجمالي" value={formatMoney(pl.grossProfit)} />
            <StatTile
              label="صافي الربح" value={formatMoney(pl.netProfit)}
              hint={pl.marginPercent != null ? `هامش صافٍ ${pl.marginPercent.toFixed(1)}%` : undefined}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="بيان مبسّط" />
              <CardBody className="space-y-2 text-sm">
                {[
                  ['الإيراد', pl.revenue, 'text-fg'],
                  ['− تكلفة البضاعة المباعة', -pl.cogs, 'text-fg-muted'],
                  ['= الربح الإجمالي', pl.grossProfit, 'font-semibold text-fg'],
                  ['− المصاريف التشغيلية', -pl.expenseTotal, 'text-fg-muted'],
                ].map(([label, value, cls]) => (
                  <div key={label as string} className="flex justify-between border-b border-border pb-2">
                    <span className={cls as string}>{label}</span>
                    <span className={cn('tabular-nums', cls as string)} dir="ltr">
                      {formatMoney(Math.abs(value as number))}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between pt-1 text-lg font-bold">
                  <span className="text-fg">= صافي الربح</span>
                  <span className={cn('tabular-nums', pl.netProfit >= 0 ? 'text-success' : 'text-danger')} dir="ltr">
                    {formatMoney(pl.netProfit)}
                  </span>
                </div>

                <Alert tone="info" className="mt-3">
                  المشتريات لا تُطرح هنا: شراء البضاعة تحويل أصل لا مصروف، وتظهر تكلفتها عند بيعها
                  ضمن «تكلفة البضاعة المباعة». طرحها مرّتين يُظهر خسارة وهمية.
                  {purchases && (
                    <> مشتريات الفترة: <span className="font-semibold" dir="ltr">{formatMoney(purchases.purchaseTotal)}</span>.</>
                  )}
                </Alert>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="المصاريف حسب البند" />
              <CardBody>
                <BarChart
                  color="var(--chart-2)"
                  data={pl.expenses.map((e) => ({ key: e.categoryName, label: e.categoryName, value: e.amount }))}
                  emptyText="لا مصاريف مسجّلة في هذه الفترة"
                />
              </CardBody>
            </Card>
          </div>
        </>
      )}

      {/* ══ المخزون ══ */}
      {tab === 'inventory' && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile label="قيمة المخزون" value={formatMoney(valuation?.costValue ?? 0)} hint="بسعر التكلفة" />
            <StatTile label="قيمته بسعر البيع" value={formatMoney(valuation?.retailValue ?? 0)} />
            <StatTile label="الربح المحتمل" value={formatMoney(valuation?.potentialProfit ?? 0)} hint="لو بيع كاملاً" />
            <StatTile label="إجمالي الوحدات" value={formatNumber(valuation?.totalUnits ?? 0)}
              hint={`${formatNumber(valuation?.productCount ?? 0)} منتجاً`} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="قيمة المخزون حسب التصنيف" />
              <CardBody>
                <BarChart
                  data={(valuation?.byCategory ?? []).map((c) => ({
                    key: c.categoryName,
                    label: c.categoryName,
                    value: c.costValue,
                    sublabel: `${formatNumber(c.units)} وحدة`,
                  }))}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="المخزون الراكد"
                description="رأس مال نائم على الرفّ"
                action={
                  <div className="flex items-center gap-2">
                    <select
                      value={stagnantDays}
                      onChange={(e) => setStagnantDays(Number(e.target.value))}
                      className="h-8 rounded-lg border border-border bg-surface px-2 text-xs text-fg-muted"
                      aria-label="المدّة"
                    >
                      {[30, 60, 90, 180, 365].map((d) => (
                        <option key={d} value={d}>{d} يوماً</option>
                      ))}
                    </select>
                    <Button variant="secondary" size="sm" onClick={() => void handleExport('stagnant')}
                      loading={downloading} startIcon={<Download className="size-4" />}>
                      CSV
                    </Button>
                  </div>
                }
              />
              <TableWrapper className="rounded-none border-0 border-t">
                <Table>
                  <Thead>
                    <tr>
                      <Th>المنتج</Th>
                      <Th align="center">الكمية</Th>
                      <Th align="end">القيمة</Th>
                      <Th align="center">منذ آخر بيع</Th>
                    </tr>
                  </Thead>
                  <Tbody>
                    {stagnant?.slice(0, 15).map((p) => (
                      <Tr key={p.productId}>
                        <Td>
                          <p className="truncate font-medium text-fg">{p.nameAr}</p>
                          <p className="font-mono text-xs text-fg-subtle" dir="ltr">{p.sku}</p>
                        </Td>
                        <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(p.quantity)}</Td>
                        <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(p.stockValue)}</Td>
                        <Td align="center">
                          {p.daysSinceLastSale === null ? (
                            <Badge tone="danger">لم يُبَع قطّ</Badge>
                          ) : (
                            <span className="tabular-nums text-fg-muted" dir="ltr">{formatNumber(p.daysSinceLastSale)} يوماً</span>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableWrapper>
            </Card>
          </div>
        </>
      )}

      {/* ══ الضريبة ══ */}
      {tab === 'tax' && tax && (
        <Card>
          <CardHeader
            title="تقرير الضريبة"
            description={`الوعاء الخاضع ${formatMoney(tax.totalTaxable)} · الضريبة ${formatMoney(tax.totalTax)}`}
            action={
              <Button variant="secondary" size="sm" onClick={() => void handleExport('tax')}
                loading={downloading} startIcon={<Download className="size-4" />}>
                CSV
              </Button>
            }
          />
          <TableWrapper className="rounded-none border-0 border-t">
            <Table>
              <Thead>
                <tr>
                  <Th>النسبة</Th>
                  <Th align="center">الفواتير</Th>
                  <Th align="end">الوعاء الخاضع</Th>
                  <Th align="end">الضريبة</Th>
                </tr>
              </Thead>
              <Tbody>
                {tax.brackets.map((b) => (
                  <Tr key={b.taxRate}>
                    <Td><Badge tone={b.taxRate === 0 ? 'neutral' : 'primary'}>{b.taxRate === 0 ? 'معفى' : `${b.taxRate}%`}</Badge></Td>
                    <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(b.invoiceCount)}</Td>
                    <Td align="end" className="tabular-nums" dir="ltr">{formatMoney(b.taxableAmount)}</Td>
                    <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(b.taxAmount)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableWrapper>
        </Card>
      )}
    </div>
  )
}
